import {ErroneousServerResult, isErroneousServerResult, useHttpClient} from '@raison/network-facade';
import {Money, TransactionId} from '@raison/types';
import React, {useState} from 'react';
import {exchangeEurUsd, exchangeUsdEur} from '~/processes/fiat-operations/api/fiat/exchangeService';
import BottomTextModal from '~/processes/fiat-operations/ui/Shared/BottomTextModal';

interface UseSubmitProps {
    readonly inputMoney: Money;
    readonly onSuccess: (id: TransactionId) => void;
}

export const useSubmit = ({inputMoney, onSuccess}: UseSubmitProps) => {
    const http = useHttpClient();
    const [serverError, setServerError] = useState<ErroneousServerResult | null>(null);

    const submit = async () => {
        const request = inputMoney.currencyCode === 'EUR' ? exchangeEurUsd : exchangeUsdEur;
        const result = await request(http, inputMoney.amount).catch(error => {
            setServerError(humanizeError(error));
            return null;
        });
        if (result === null) {
            return false;
        }
        const {status, json} = result;
        if (status === 'Abort') {
            return false;
        }
        if (status === 400) {
            setServerError(humanizeError(json));
            return false;
        }
        onSuccess(json.id);
        return true;
    };

    const renderServerErrorModal = () => {
        return (
            <BottomTextModal
                onClose={() => {
                    setServerError(null);
                }}
                isOpen={Boolean(serverError)}
                heading='Something went wrong'
                text={serverError?.message ?? ''}
            />
        );
    };

    return {submit, renderServerErrorModal};
};

const humanizeError = (error: unknown) => {
    if (!isErroneousServerResult(error)) {
        if (!navigator.onLine) {
            return {code: 0, message: offlineUiError};
        } else {
            return {code: 0, message: genericUiError};
        }
    }

    if (error.code === 400) {
        return {code: error.code, message: error.message};
    }

    return {code: error.code, message: genericUiError};
};

const genericUiError =
    "We’re sorry that you have to experience some problems. Don't worry, we'll fix it. Please try again later.";

const offlineUiError = 'Please check your internet connection and try again.';
